import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Box, FormControl, FormLabel, FormErrorMessage, Textarea } from '@chakra-ui/react';
import { CKEditor } from "@ckeditor/ckeditor5-react";
import './CKEditorWidget.css';
import {
  ClassicEditor,
  Alignment,
  Autoformat,
  Bold,
  Code,
  Italic,
  Strikethrough,
  Subscript,
  Superscript,
  Underline,
  BlockQuote,
  CodeBlock,
  Essentials,
  FindAndReplace,
  Font,
  Heading,
  Highlight,
  HorizontalLine,
  GeneralHtmlSupport,
  AutoImage,
  Image,
  ImageCaption,
  ImageInsert,
  ImageResize,
  ImageStyle,
  ImageToolbar,
  ImageUpload,
  SimpleUploadAdapter,
  PictureEditing,
  Indent,
  IndentBlock,
  AutoLink,
  Link,
  LinkImage,
  List,
  ListProperties,
  TodoList,
  MediaEmbed,
  Mention,
  PageBreak,
  Paragraph,
  PasteFromOffice,
  RemoveFormat,
  SourceEditing,
  SpecialCharacters,
  SpecialCharactersEssentials,
  Style,
  Table,
  TableCaption,
  TableCellProperties,
  TableColumnResize,
  TableProperties,
  TableToolbar,
  TextPartLanguage,
  TextTransformation,
  WordCount
} from "ckeditor5";

import "ckeditor5/ckeditor5.css";

const LICENSE_KEY = "GPL";

const CKEditorWidget = ({
  id,
  value,
  onChange,
  onBlur,
  onFocus,
  label,
  required,
  disabled,
  readonly,
  schema,
  uiSchema,
  rawErrors,
  options,
}) => {
  const [editorValue, setEditorValue] = useState(value || '');
  const [rawHtml, setRawHtml] = useState(value || '');
  const [isFocused, setIsFocused] = useState(false);
  const editorRef = useRef(null);
  const lastExternalValue = useRef(value);
  const isInternalChange = useRef(false);

  // Get configuration from uiSchema
  const config = React.useMemo(() => {
    const uiConfig = uiSchema?.['ui:options'] || {};
    return {
      placeholder: uiConfig.placeholder || 'Type or paste your content here!',
      height: uiConfig.height || '420px',
      readOnly: readonly || disabled,
    };
  }, [uiSchema, readonly, disabled]);

  // Update from external changes
  useEffect(() => {
    if (value !== lastExternalValue.current && !isInternalChange.current) {
      setEditorValue(value || '');
      setRawHtml(value || '');
      lastExternalValue.current = value;
    }
  }, [value]);

  const handleEditorChange = useCallback((event, editor) => {
    isInternalChange.current = true;
    let htmlContent = editor.getData();

    // Remove <p> wrappers around standalone images
    htmlContent = htmlContent.replace(/<p[^>]*>\s*(<img[^>]+>)\s*<\/p>/gi, '$1');

    // CKEditor moves width from <img> style to <figure> style during its model conversion.
    // Unwrap <figure class="image"> that contains only an <img> (no figcaption),
    // and merge the figure's inline styles back into the <img> so width:100% etc. are preserved.
    htmlContent = htmlContent.replace(
      /<figure\b([^>]*)>\s*(<img\b[^>]*\/?>)\s*<\/figure>/gi,
      (_match, figAttrs, imgTag) => {
        const figStyleMatch = figAttrs.match(/style="([^"]*)"/i);
        const figStyle = figStyleMatch ? figStyleMatch[1].trim() : '';
        if (!figStyle) return imgTag;
        const imgStyleMatch = imgTag.match(/style="([^"]*)"/i);
        if (imgStyleMatch) {
          const merged = figStyle.replace(/;$/, '') + ';' + imgStyleMatch[1];
          return imgTag.replace(/style="[^"]*"/i, `style="${merged}"`);
        }
        return imgTag.replace(/^<img\b/i, `<img style="${figStyle}"`);
      }
    );
    
    setEditorValue(htmlContent);
    setRawHtml(htmlContent);
    
    if (onChange) {
      onChange(htmlContent);
    }
    
    lastExternalValue.current = htmlContent;
    
    // Reset the flag after the change is processed
    setTimeout(() => {
      isInternalChange.current = false;
    }, 0);
  }, [onChange]);

  const handleRawHtmlChange = useCallback((e) => {
    const newHtml = e.target.value;
    isInternalChange.current = true;
    
    // Only update the raw HTML state, don't update the editor
    setRawHtml(newHtml);
    
    if (onChange) {
      onChange(newHtml);
    }
    
    lastExternalValue.current = newHtml;
    
    setTimeout(() => {
      isInternalChange.current = false;
    }, 0);
  }, [onChange]);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
    if (onFocus) {
      onFocus(id, editorValue);
    }
  }, [onFocus, id, editorValue]);

  const handleBlur = useCallback((e) => {
    setIsFocused(false);
    
    // Sync the editor with the raw HTML when blurring from textarea
    if (e && e.target && e.target.tagName === 'TEXTAREA') {
      setEditorValue(rawHtml);
    }
    
    if (onBlur) {
      onBlur(id, rawHtml);
    }
  }, [onBlur, id, rawHtml]);

  return (
    <FormControl
      isInvalid={rawErrors && rawErrors.length > 0}
      isRequired={required}
      isDisabled={disabled}
    >
      <style>
        {`
          .ck-editor-wrapper .ck-editor__editable { 
            min-height: ${config.height};
            max-height: ${config.height};
            height: ${config.height};
          }
        `}
      </style>
      
      {label && (
        <FormLabel htmlFor={id} mb={2}>
          {label}
        </FormLabel>
      )}
      
      <Box
        className={`ck-editor-wrapper ${isFocused ? 'focused' : ''}`}
        onFocus={handleFocus}
        onBlur={handleBlur}
      >
        <CKEditor
          ref={editorRef}
          editor={ClassicEditor}
          data={editorValue || ''}
          disabled={config.readOnly}
          config={{
            licenseKey: LICENSE_KEY,
            placeholder: config.placeholder,
            plugins: [
              Alignment,
              Autoformat,
              AutoImage,
              AutoLink,
              BlockQuote,
              Bold,
              Code,
              CodeBlock,
              Essentials,
              FindAndReplace,
              Font,
              Heading,
              Highlight,
              HorizontalLine,
              GeneralHtmlSupport,
              Image,
              ImageCaption,
              ImageInsert,
              ImageResize,
              ImageStyle,
              ImageToolbar,
              ImageUpload,
              SimpleUploadAdapter,
              Indent,
              IndentBlock,
              Italic,
              Link,
              LinkImage,
              List,
              ListProperties,
              MediaEmbed,
              Mention,
              PageBreak,
              Paragraph,
              PasteFromOffice,
              PictureEditing,
              RemoveFormat,
              SourceEditing,
              SpecialCharacters,
              SpecialCharactersEssentials,
              Strikethrough,
              Style,
              Subscript,
              Superscript,
              Table,
              TableCaption,
              TableCellProperties,
              TableColumnResize,
              TableProperties,
              TableToolbar,
              TextPartLanguage,
              TextTransformation,
              TodoList,
              Underline,
              WordCount
            ],
            toolbar: {
              shouldNotGroupWhenFull: true,
              items: [
                "undo",
                "redo",
                "|",
                "sourceEditing",
                "|",
                "findAndReplace",
                "selectAll",
                "|",
                "link",
                "insertImage",
                "insertTable",
                "blockQuote",
                "mediaEmbed",
                "codeBlock",
                "pageBreak",
                "horizontalLine",
                "specialCharacters",
                "-",
                "heading",
                "|",
                "bold",
                "italic",
                "underline",
                "strikethrough",
                {
                  label: "Basic styles",
                  icon: "text",
                  items: [
                    "fontSize",
                    "fontFamily",
                    "fontColor",
                    "fontBackgroundColor",
                    "highlight",
                    "superscript",
                    "subscript",
                    "code",
                    "|",
                    "textPartLanguage",
                    "|"
                  ]
                },
                "removeFormat",
                "|",
                "alignment",
                "|",
                "bulletedList",
                "numberedList",
                "todoList",
                "|",
                "outdent",
                "indent"
              ]
            },
            htmlSupport: {
              allow: [
                {
                  name: /.*/,
                  attributes: true,
                  classes: true,
                  styles: true
                },
                // Explicitly allow style tags for media queries and custom CSS
                {
                  name: 'style',
                  attributes: true
                },
                // Explicitly allow script tags for custom JavaScript
                {
                  name: 'script',
                  attributes: true
                }
              ],
              disallow: [
                {
                  attributes: [
                    { key: /^on(.*)/i, value: true },
                    { key: /.*/, value: /data:(?!image\/(png|jpeg|gif|webp))/i }
                  ]
                }
              ]
            },
            fontFamily: {
              supportAllValues: true
            },
            fontSize: {
              options: [10, 12, 14, "default", 18, 20, 22, 24, 28, 32],
              supportAllValues: true
            },
            list: {
              properties: {
                styles: true,
                startIndex: true,
                reversed: true
              }
            },
            table: {
              contentToolbar: [
                "tableColumn",
                "tableRow",
                "mergeTableCells",
                "tableProperties",
                "tableCellProperties",
                "toggleTableCaption"
              ],
              defaultHeadings: { rows: 1, columns: 1 },
              tableProperties: {
                defaultProperties: {
                  borderStyle: 'solid',
                  borderColor: '#bfbfbf',
                  borderWidth: '1px',
                  alignment: 'left',
                  width: 'auto',
                  height: 'auto'
                }
              },
              tableCellProperties: {
                defaultProperties: {
                  verticalAlignment: 'top',
                  horizontalAlignment: 'left',
                  padding: '0.4em',
                  width: 'auto',
                  height: 'auto'
                }
              }
            },
            image: {
              resizeOptions: [
                { name: "resizeImage:original", label: "Default", value: null },
                { name: "resizeImage:50", label: "50%", value: "50" },
                { name: "resizeImage:75", label: "75%", value: "75" },
                { name: "resizeImage:100", label: "100%", value: "100" }
              ],
              toolbar: [
                "imageTextAlternative",
                "toggleImageCaption",
                "|",
                "imageStyle:inline",
                "imageStyle:wrapText",
                "imageStyle:breakText",
                "|",
                "resizeImage"
              ],
              insert: { integrations: ["url"] }
            },
            mediaEmbed: { 
              previewsInData: true 
            },
            simpleUpload: {
              uploadUrl: "http://localhost:3001/upload"
            }
          }}
          onReady={(editor) => {
            editorRef.current = editor;
            editor.editing.view.focus();
          }}
          onChange={handleEditorChange}
        />
      </Box>
      
      <Box mt={2}>
        <Textarea
          value={rawHtml}
          onChange={handleRawHtmlChange}
          placeholder="Raw HTML"
          fontFamily="monospace"
          fontSize="sm"
          minHeight="200px"
          readOnly={false}
          isDisabled={false}
          disabled={false}
          onFocus={handleFocus}
          onBlur={handleBlur}
          border={isFocused ? '2px solid' : '1px solid'}
          borderColor={isFocused ? 'blue.500' : 'gray.300'}
          backgroundColor="white"
          cursor="text"
          opacity={0.8}
        />
      </Box>
      
      {rawErrors && rawErrors.length > 0 && (
        <FormErrorMessage>
          {rawErrors.map((error, index) => (
            <span key={index}>{error}</span>
          ))}
        </FormErrorMessage>
      )}
    </FormControl>
  );
};

export default CKEditorWidget;
